const DEFAULT_WORKER_HTTP_URL = 'http://127.0.0.1:8787';

export function workerHttpUrl(): string {
  return (import.meta.env.VITE_WORKER_URL as string | undefined) ?? DEFAULT_WORKER_HTTP_URL;
}

function socketOrigin(): { protocol: string; host: string } {
  const httpUrl = new URL(workerHttpUrl());
  return { protocol: httpUrl.protocol === 'https:' ? 'wss:' : 'ws:', host: httpUrl.host };
}

/**
 * The old `ranked` option is gone (decision D2). A client cannot declare its own
 * match ranked; it can only forward the single-use ticket that the ranked queue
 * issued to it, which the server checks against the authorisation it holds for
 * that room. A private room simply has no ticket, and plays as casual.
 */
/**
 * `connectionTicket` is a short-lived, single-use ticket from
 * `/api/connection-ticket`, used in place of the long-lived session token, which
 * must never appear in a socket URL. `ticket` is the separate ranked-match
 * authorisation the queue issues.
 */
export function workerSocketUrl(
  code: string,
  options: { connectionTicket?: string | null; ticket?: string | null } = {},
): string {
  const { protocol, host } = socketOrigin();
  const url = new URL(`${protocol}//${host}/api/match/${code}/socket`);
  if (options.connectionTicket) url.searchParams.set('ct', options.connectionTicket);
  if (options.ticket) url.searchParams.set('ticket', options.ticket);
  return url.toString();
}

export function workerRankedQueueUrl(connectionTicket: string | null): string {
  const { protocol, host } = socketOrigin();
  const url = new URL(`${protocol}//${host}/api/ranked/queue`);
  if (connectionTicket) url.searchParams.set('ct', connectionTicket);
  return url.toString();
}

export function workerKnockoutQueueUrl(connectionTicket: string | null): string {
  const { protocol, host } = socketOrigin();
  const url = new URL(`${protocol}//${host}/api/knockout/queue`);
  if (connectionTicket) url.searchParams.set('ct', connectionTicket);
  return url.toString();
}

export function workerKnockoutSocketUrl(code: string, connectionTicket: string | null): string {
  const { protocol, host } = socketOrigin();
  const url = new URL(`${protocol}//${host}/api/knockout/${code}/socket`);
  if (connectionTicket) url.searchParams.set('ct', connectionTicket);
  return url.toString();
}

/**
 * Stack Tower's head-to-head DO always takes a display `name` (used for a
 * guest, or as the fallback if `connectionTicket` is absent/invalid); a
 * signed-in player additionally passes a connection ticket (C015 Phase 5,
 * same hardening as the 1v1 duel — never the long-lived session token in a
 * socket URL), which the server resolves to their real username and to the
 * identity match-history recording and a real reconnect grace need. `ticket`
 * (C015 Phase 6) is the separate ranked-match authorisation the queue issues
 * — forwarding it is the only thing that can make a match ranked (D2).
 */
export function workerStackTowerMatchSocketUrl(
  code: string,
  name: string,
  connectionTicket: string | null = null,
  ticket: string | null = null,
  mode: 'timed' | 'endless' | null = null,
): string {
  const { protocol, host } = socketOrigin();
  const url = new URL(`${protocol}//${host}/api/stack-tower/match/${code}/socket`);
  url.searchParams.set('name', name);
  if (connectionTicket) url.searchParams.set('ct', connectionTicket);
  if (ticket) url.searchParams.set('ticket', ticket);
  // Only meaningful on a room's first-ever connection — the DO commits to a
  // mode then and ignores this param on every later connection (own comment
  // in stack-tower-match.ts). Omitted (not 'timed') defaults to timed, same
  // as every pre-Phase-3 client that has never sent this param.
  if (mode) url.searchParams.set('mode', mode);
  return url.toString();
}

export function workerStackTowerRankedQueueUrl(connectionTicket: string | null): string {
  const { protocol, host } = socketOrigin();
  const url = new URL(`${protocol}//${host}/api/stack-tower/ranked/queue`);
  if (connectionTicket) url.searchParams.set('ct', connectionTicket);
  return url.toString();
}
